import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Row, Col, Badge } from "reactstrap";
import {
  Table,
  Tag,
  Space,
  Modal,
  message,
  Card,
  Descriptions,
  Divider,
  Alert,
  Popover,
  Timeline,
} from "antd";
import {
  CheckCircleOutlined,
  PrinterOutlined,
  EditOutlined,
  DownOutlined,
  RightOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Icon } from "../../../components/Component";
import { WorkRequestFormData, WorkRequestLine, WorkLineSource } from "../types";
import workRequestApis from "../../../api/WorkOrderManagement/WorkRequestApis";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router";
import rolesApi from "../../../api/master/roles";

const WorkRequestPreview: React.FC = (props: any) => {
  const { handleSubmit, watch } = useFormContext<WorkRequestFormData>();
  const [submitting, setSubmitting] = useState(false);
  const [originalData, setOriginalData] = useState<any>(null);
  const Navigate = useNavigate();
  const location = useLocation();
  // Watch all form values
  const formData = watch();
  const workRequestLines: WorkRequestLine[] = formData.workRequestLines || [];
  const isEditMode = formData.type === "edit";
  const [roles, setRoles] = useState<any>([]);

  const getRequestForDisplay = (value: string) => {
    return value; // Already in display format from enum
  };

  const fetchApprovalHierarchyUsersByModuleId = async (moduleID: number, siteId: number) => {
    try {
      const res = await rolesApi.fetchHierarchyUsersByModuleId(
        moduleID,
        // Number(formData.budgetHeader.site)
        siteId
      );
      if (!res) return;
      const { data, status } = res;
      if (status) {
        setRoles(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchModuleConfig = async () => {
    try {
      const res = await rolesApi.getModuleConfig();
      let moduleID: number | undefined;

      const procureToPayCategory = res?.data?.find(
        (category: any) => category.category === "ProcureToPay"
      );


      if (procureToPayCategory && procureToPayCategory.modules) {
        const budgetModule = procureToPayCategory.modules.find(
          (module: any) => module.moduleName === "Budget"
        );


        if (budgetModule) {
          moduleID = budgetModule.moduleID;
        }
      }


      if (moduleID && formData?.siteId) {
        await fetchApprovalHierarchyUsersByModuleId(moduleID, Number(formData?.siteId));
      } else {
        console.warn("Module ID or Site ID is missing", {
          moduleID,
          site: formData?.siteId
        });
        setRoles([]);
      }
    } catch (err) {
      console.error("Error fetching module config:", err);
      setRoles([]);
    }
  };

  useEffect(() => {
    fetchModuleConfig();
  }, []);

  const getApprovalDisplay = (value: string) => {
    return value === "true" ? "Yes" : "No";
  };

  const formatDate = (date: any) => {
    if (!date) return "-";
    return dayjs(date).format("MM/DD/YYYY");
  };

  const getSourceColor = (source: WorkLineSource) => {
    const colors = {
      [WorkLineSource.ProjectManagement]: "blue",
      [WorkLineSource.WorkBOQ]: "green",
      [WorkLineSource.NewWorkLine]: "orange",
    };
    return colors[source] || "default";
  };

  const getSourceDisplayName = (source: string) => {
    switch (source) {
      case "Project Management":
        return "Project Mgmt";
      case "Work BOQ":
        return "Work BOQ";
      case "New Work Line":
        return "New Work";
      default:
        return source;
    }
  };

  // Material BOQ, Documents, and TaskBOQs expandable row component for preview
  const expandedRowRender = (record: WorkRequestLine) => {
    const materialAllocation = (record as any).materialAllocation;
    const documents = record.documents || [];
    const taskBoqs = (record as any).taskBoqs || [];
    const isWorkBOQ = record.source === WorkLineSource.WorkBOQ;
    const isProjectManagement =
      record.source === WorkLineSource.ProjectManagement;

    // Show material allocation, documents, or taskBoqs based on source
    const showMaterialAllocation =
      materialAllocation && materialAllocation.length > 0;
    const showDocuments = isWorkBOQ && documents.length > 0;
    const showTaskBoqs = isProjectManagement && taskBoqs.length > 0;

    if (!showMaterialAllocation && !showDocuments && !showTaskBoqs) {
      return null;
    }

    const materialColumns = [
      {
        title: "Material Name",
        dataIndex: "materialName",
        key: "materialName",
        width: "40%",
        render: (name: string, material: any) => (
          <div>
            <div className="fw-bold">{name}</div>
            {material.categoryName && (
              <Tag color="blue" style={{ marginTop: 4 }}>
                {material.categoryName}
              </Tag>
            )}
          </div>
        ),
      },
      {
        title: "Quantity",
        dataIndex: "allocatedQty",
        key: "allocatedQty",
        width: "20%",
        align: "right" as const,
        render: (qty: number) => <strong>{qty?.toLocaleString()}</strong>,
      },
      {
        title: "UOM",
        dataIndex: "uomName",
        key: "uomName",
        width: "20%",
        align: "center" as const,
        render: (uom: string) => <Tag color="geekblue">{uom}</Tag>,
      },
      {
        title: "Material ID",
        dataIndex: "materialId",
        key: "materialId",
        width: "20%",
        render: (id: string) => <code className="text-muted">{id}</code>,
      },
    ];

    const documentColumns = [
      {
        title: "Document Name",
        dataIndex: "name",
        key: "name",
        width: "50%",
        render: (name: string, doc: any) => (
          <div>
            <div className="fw-bold">
              {name || doc.documentName || doc.fileName}
            </div>
            {doc.description && (
              <small className="text-muted d-block">{doc.description}</small>
            )}
          </div>
        ),
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: "20%",
        render: (type: string, doc: any) => (
          <Tag color="cyan">{type || doc.fileType || "Document"}</Tag>
        ),
      },
      {
        title: "Size",
        dataIndex: "size",
        key: "size",
        width: "15%",
        render: (size: any, doc: any) => {
          const fileSize = size || doc.fileSize;
          if (fileSize) {
            const sizeInMB =
              typeof fileSize === "number"
                ? (fileSize / (1024 * 1024)).toFixed(2)
                : fileSize;
            return <span className="text-muted">{sizeInMB} MB</span>;
          }
          return <span className="text-muted">-</span>;
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: "15%",
        render: (_: any, doc: any) => (
          <Button
            // type="text"
            size="small"
            onClick={() => {
              if (doc.url || doc.documentUrl) {
                window.open(doc.url || doc.documentUrl, "_blank");
              } else {
                message.info("Document URL not available");
              }
            }}
          >
            View
          </Button>
        ),
      },
    ];

    const taskBoqColumns = [
      {
        title: "Material Name",
        dataIndex: "materialName",
        key: "materialName",
        width: "40%",
        render: (name: string, material: any) => (
          <div>
            <div className="fw-bold">{name}</div>
            {material.categoryName && (
              <Tag color="blue" style={{ marginTop: 4 }}>
                {material.categoryName}
              </Tag>
            )}
          </div>
        ),
      },
      {
        title: "Allocated Qty",
        dataIndex: "allocatedQty",
        key: "allocatedQty",
        width: "20%",
        align: "right" as const,
        render: (qty: number) => <strong>{qty?.toLocaleString()}</strong>,
      },
      {
        title: "UOM",
        dataIndex: "uomName",
        key: "uomName",
        width: "20%",
        align: "center" as const,
        render: (uom: string) => <Tag color="geekblue">{uom}</Tag>,
      },
    ];

    return (
      <div
        style={{
          margin: "16px 0",
          backgroundColor: "#fafafa",
          padding: "16px",
          borderRadius: "6px",
        }}
      >
        {showMaterialAllocation && (
          <div className="mb-3">
            <div className="mb-2">
              <strong>
                📦 Material BOQ Allocation ({materialAllocation.length} items)
              </strong>
            </div>
            <Table
              columns={materialColumns}
              dataSource={materialAllocation}
              pagination={false}
              size="small"
              rowKey="workBoqLineMetId"
              bordered
            />
          </div>
        )}

        {showDocuments && (
          <div className="mb-3">
            <div className="mb-2">
              <strong>📄 Documents ({documents.length} files)</strong>
            </div>
            <Table
              columns={documentColumns}
              dataSource={documents.map((doc: any, index: number) =>
                typeof doc === "string"
                  ? { id: index, name: doc, type: "Document", url: doc }
                  : { id: index, ...doc }
              )}
              pagination={false}
              size="small"
              rowKey={(record, index) =>
                record.id || record.documentId || index
              }
              bordered
            />
          </div>
        )}

        {showTaskBoqs && (
          <div>
            <div className="mb-2">
              <strong>📦 Material Allocation ({taskBoqs.length} items)</strong>
            </div>
            <Table
              columns={taskBoqColumns}
              dataSource={taskBoqs}
              pagination={false}
              size="small"
              rowKey={(record, index) =>
                record.materialId || record.id || index
              }
              bordered
            />
          </div>
        )}
      </div>
    );
  };

  // Get columns based on source - matching workRequestDetails format
  const getColumnsForSource = () => {
    const baseColumns = [
      {
        title: "S.No.",
        key: "index",
        width: 60,
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: "Task Title",
        dataIndex: "taskTitle",
        key: "taskTitle",
        width: 200,
        render: (text: string, record: WorkRequestLine) => (
          <div>
            <div className="fw-bold">{text}</div>
            {record.taskDescription && (
              <small className="text-muted">{record.taskDescription}</small>
            )}
          </div>
        ),
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        width: 130,
        align: "right" as const,
        render: (quantity: number, record: WorkRequestLine) => {
          // Show available vs requested quantity
          const availableQty = (record as any).availableQuantity || quantity;
          const requestedQty = (record as any).requestedQuantity || quantity;

          return (
            <div>
              <div className="text-muted small">
                Available: {availableQty.toLocaleString()}
              </div>
              <div>
                <strong>Requested: {requestedQty.toLocaleString()}</strong>{" "}
                {record.uom}
              </div>
            </div>
          );
        },
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        width: 140,
        render: (source: WorkLineSource, record: WorkRequestLine) => {
          const colors = {
            [WorkLineSource.ProjectManagement]: "blue",
            [WorkLineSource.WorkBOQ]: "green",
            [WorkLineSource.NewWorkLine]: "orange",
          };
          const materialAllocation = (record as any).materialAllocation;
          const documents = record.documents || [];
          const taskBoqs = (record as any).taskBoqs || [];
          const hasMaterialBOQ =
            materialAllocation && materialAllocation.length > 0;
          const hasDocuments =
            source === WorkLineSource.WorkBOQ && documents.length > 0;
          const hasTaskBoqs =
            source === WorkLineSource.ProjectManagement && taskBoqs.length > 0;

          return (
            <div>
              <Tag color={colors[source]}>{source}</Tag>
              {hasMaterialBOQ && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="purple" style={{ fontSize: "10px" }}>
                    📦 Material BOQ
                  </Tag>
                </div>
              )}
              {hasDocuments && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="cyan" style={{ fontSize: "10px" }}>
                    📄 Documents
                  </Tag>
                </div>
              )}
              {hasTaskBoqs && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="purple" style={{ fontSize: "10px" }}>
                    📦 Materials
                  </Tag>
                </div>
              )}
            </div>
          );
        },
      },
    ];

    // Add Category column for non-ProjectManagement sources
    const hasNonProjectManagementLines = workRequestLines.some(
      (line) => line.source !== WorkLineSource.ProjectManagement
    );
    if (hasNonProjectManagementLines) {
      baseColumns.splice(2, 0, {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 120,
        render: (text: string, record: WorkRequestLine) => {
          if (record.source === WorkLineSource.ProjectManagement) {
            return "-";
          }
          return (
            <div>
              <div>{text}</div>
              {record.subcategory && (
                <small className="text-muted">{record.subcategory}</small>
              )}
            </div>
          );
        },
      } as any);
    }

    // Add Tower/Villa column for non-ProjectManagement sources
    if (hasNonProjectManagementLines) {
      baseColumns.push({
        title: "Tower/Villa",
        key: "location",
        width: 120,
        render: (_: any, record: WorkRequestLine) => {
          if (record.source === WorkLineSource.ProjectManagement) {
            return "-";
          }
          if (record.assignedTower) return record.assignedTower;
          if (record.assignedVilla) return record.assignedVilla;
          return "-";
        },
      } as any);
    }

    // Add Activity column for ProjectManagement sources
    const hasProjectManagementLines = workRequestLines.some(
      (line) => line.source === WorkLineSource.ProjectManagement
    );
    if (hasProjectManagementLines) {
      baseColumns.push({
        title: "Activity",
        key: "activity",
        width: 150,
        render: (_: any, record: WorkRequestLine) => {
          if (record.source === WorkLineSource.ProjectManagement) {
            return (
              <div>
                <div className="fw-bold text-primary">
                  {(record as any).activityName || "-"}
                </div>
                <small className="text-muted">Project Activity</small>
              </div>
            );
          }
          return "-";
        },
      } as any);
    }

    // Add Planned Dates column only if not all lines are from Work BOQ
    const hasNonWorkBOQLines = workRequestLines.some(
      (line) => line.source !== WorkLineSource.WorkBOQ
    );
    if (hasNonWorkBOQLines) {
      baseColumns.push({
        title: "Planned Dates",
        key: "plannedDates",
        width: 150,
        render: (_: any, record: WorkRequestLine) => {
          // Don't show planned dates for Work BOQ lines
          if (record.source === WorkLineSource.WorkBOQ) {
            return <span className="text-muted">-</span>;
          }

          return (
            <div>
              {record.plannedStartDate && (
                <div>
                  <small>Start: {record.plannedStartDate}</small>
                </div>
              )}
              {record.plannedEndDate && (
                <div>
                  <small>End: {record.plannedEndDate}</small>
                </div>
              )}
              {!record.plannedStartDate && !record.plannedEndDate && "-"}
            </div>
          );
        },
      } as any);
    }

    return baseColumns;
  };

  // Work Request Lines Table Columns - Dynamic based on source
  const workLinesColumns = getColumnsForSource();

  const validateFinalSubmission = (data: WorkRequestFormData) => {
    // Final validation before submission
    if (workRequestLines.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please add at least one work request line",
      });
      return false;
    }

    if (!data.workRequestTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Request Title is required",
      });
      return false;
    }

    if (!data.siteId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Site selection is required",
      });
      return false;
    }

    return true;
  };

  // Helper function to detect changes and prepare update payload
  const prepareUpdatePayload = (
    data: WorkRequestFormData,
    isDraft: boolean = false
  ) => {
    const updatePayload: any = {};

    // Get workRequestId from the current form data (should be set during edit)
    const workRequestId = (data as any).workRequestId || (data as any).id;
    if (!workRequestId) {
      throw new Error("Work Request ID is required for update");
    }
    updatePayload.workRequestId = workRequestId;

    // Only include changed fields
    if (data.workRequestTitle)
      updatePayload.workRequestTitle = data.workRequestTitle;
    if (data.description) updatePayload.description = data.description;
    if (data.siteId) updatePayload.siteId = Number(data.siteId);
    if (data.projectId) updatePayload.projectId = Number(data.projectId);
    if (data.requestFor) updatePayload.requestFor = data.requestFor;
    if (data.plannedStartDate) {
      updatePayload.plannedStartDate = dayjs(data.plannedStartDate).format(
        "YYYY-MM-DD"
      );
    }
    if (data.plannedEndDate) {
      updatePayload.plannedEndDate = dayjs(data.plannedEndDate).format(
        "YYYY-MM-DD"
      );
    }
    if (data.approvalRequired !== undefined) {
      updatePayload.approvalRequired = data.approvalRequired === "true";
    }

    if (data.approvalRequired === "true" && data.isStrictApproval !== undefined) {
      updatePayload.isStrictApproval = Boolean(data.isStrictApproval);
    }

    // Set status based on isDraft flag
    updatePayload.status = isDraft ? "Draft" : "Submitted";

    // Process work request lines for update
    const newLines: any[] = [];
    const updatedLines: any[] = [];
    const deletedLineIds: number[] = [];

    workRequestLines.forEach((line: any) => {
      const lineData = {
        sourceType:
          line.source === "Project Management"
            ? "Project Tasks"
            : line.source === "New Work Line"
              ? "New Work Line"
              : "Work BOQ",
        sourceId: line.sourceId ? Number(line.sourceId) : null,
        workCategoryId: line.workCategoryId
          ? Number(line.workCategoryId)
          : null,
        workSubCategoryId: line.workSubCategoryId
          ? Number(line.workSubCategoryId)
          : null,
        taskTitle: line.taskTitle || "",
        taskDescription: line.taskDescription || "",
        quantity: Number(line.quantity) || 0,
        uomId: line.uomId ? Number(line.uomId) : null,
        towerVilla: line.assignedTower || line.towerVilla || null,
        floorName: line.floorFlat ? line.floorFlat.split(" ")[0] || "" : "",
        flatName: line.floorFlat
          ? line.floorFlat.split(" ").slice(1).join(" ") || "Floor 1"
          : "Floor 1",
      };

      if (line.workRequestLineId) {
        // Existing line - add to updated lines
        updatedLines.push({
          workRequestLineId: Number(line.workRequestLineId),
          ...lineData,
        });
      } else {
        // New line - add to new lines
        newLines.push(lineData);
      }
    });

    // Set work request lines as JSON string
    const allLines = [...updatedLines, ...newLines];
    if (allLines.length > 0) {
      updatePayload.workRequestLines = JSON.stringify(allLines);
    }

    // Handle deleted lines (you would need to track this separately)
    // For now, we'll use an empty array
    updatePayload.deletedLineIds = JSON.stringify(deletedLineIds);

    // Process approval hierarchy
    let approvalHierarchy = [];
    try {
      if (data.approvalsData) {
        const approvalsData = JSON.parse(data.approvalsData);
        const rawApprovals = approvalsData.data || [];
        approvalHierarchy = rawApprovals.map((approval: any) => ({
          userId: Number(approval.userId),
          level: approval.level,
          approvalStage: approval.approvalStage,
        }));
      }
    } catch (error) {
      console.log("Error parsing approvals data:", error);
    }

    if (approvalHierarchy.length > 0) {
      updatePayload.approvalHierarchy = JSON.stringify(approvalHierarchy);
    }

    // Handle file data (empty for now)
    updatePayload.filesData = JSON.stringify([]);
    updatePayload.deletedDocIds = JSON.stringify([]);

    return updatePayload;
  };

  const updateWorkRequest = async (
    data: WorkRequestFormData,
    isDraft: boolean = false
  ) => {
    if (!validateFinalSubmission(data)) return;

    try {
      setSubmitting(true);
      console.log("Updating Work Request with data:", data);

      const updatePayload = prepareUpdatePayload(data, isDraft);
      console.log("Update Payload:", updatePayload);

      // Call the update API
      const response: any = await workRequestApis.updateWorkRequest(
        updatePayload
      );

      if (response && response.status) {
        const successMessage = isDraft
          ? "Work Request updated as draft successfully!"
          : "Work Request submitted successfully!";
        message.success(successMessage);

        // Show success modal
        Swal.fire({
          title: isDraft
            ? "Work Request Updated!"
            : "Work Request Submitted Successfully!",
          text: isDraft
            ? "Your changes have been saved as draft."
            : "Your work request has been submitted for approval.",
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Go to Work Request List",
          cancelButtonText: "Continue Editing",
          confirmButtonColor: "#28a745",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed) {
            const searchParams = new URLSearchParams(location.search);
            const flowType = searchParams.get("flowType");
            const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
            Navigate(`/work-request-list${flowTypeQuery}`);
          }
        });
      } else {
        throw new Error(response?.message || "Failed to update work request");
      }
    } catch (error: any) {
      console.error("Error updating work request:", error);
      const errorMessage = isDraft
        ? "Failed to update work request as draft"
        : "Failed to submit work request";
      message.error(errorMessage);
      Swal.fire({
        icon: "error",
        title: isDraft ? "Update Failed" : "Submission Failed",
        text:
          error.message ||
          (isDraft
            ? "Failed to update work request. Please try again."
            : "Failed to submit work request. Please try again."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitForm = async (
    data: WorkRequestFormData,
    isDraft: boolean = false
  ) => {
    // Use update function for edit mode, create for new work requests
    if (isEditMode) {
      return updateWorkRequest(data, isDraft);
    }

    if (!validateFinalSubmission(data)) return;

    try {
      setSubmitting(true);

      console.log("Work Request Form Data:", data);

      const isStrictApproval = data.approvalRequired === "true"
        ? (formData?.isStrictApproval || false)
        : false;

      // Parse and transform approvals data to required format
      let approvalHierarchy = [];
      try {
        if (data.approvalsData) {
          const approvalsData = JSON.parse(data.approvalsData);
          const rawApprovals = approvalsData.data || [];

          // Transform to required format: { userId, level, approvalStage }
          approvalHierarchy = rawApprovals.map((approval: any) => ({
            userId: Number(approval.userId),
            level: approval.level,
            approvalStage: approval.approvalStage,
          }));
        }
      } catch (error) {
        console.log("Error parsing approvals data:", error);
      }

      // Transform work lines to API format with proper workCategoryId mapping
      const transformedWorkLines = workRequestLines.map((line: any) => {
        // Get workCategoryId from different sources based on line source
        let workCategoryId = null;
        let workSubCategoryId = null;

        if (line.source === "Work BOQ") {
          // For WorkBOQ lines, use the preserved IDs from BOQ data
          workCategoryId = line.workCategoryId || null;
          workSubCategoryId = line.workSubCategoryId || null;
        } else if (line.source === "New Work Line") {
          // For New Work Lines, use the stored category IDs
          workCategoryId = line.workCategoryId || null;
          workSubCategoryId = line.workSubCategoryId || null;
        } else if (line.source === "Project Management") {
          // For Project Management lines, they don't have workCategoryId
          workCategoryId = line.category;
          workSubCategoryId = null;
        }

        return {
          sourceType:
            line.source === "Project Management"
              ? "Project Tasks"
              : line.source === "New Work Line"
                ? "New Work Line"
                : "Work BOQ",
          sourceId: line.sourceId ? Number(line.sourceId) : null,
          workCategoryId: workCategoryId ? Number(workCategoryId) : null,
          workSubCategoryId: workSubCategoryId
            ? Number(workSubCategoryId)
            : null,
          taskTitle: line.taskTitle || "",
          taskDescription: line.taskDescription || "",
          quantity: Number(line.quantity) || 0,
          uomId: line.uomId ? Number(line.uomId) : null,
          towerVilla: line.assignedTower || line.towerVilla || null,
          floor: line.floorFlat ? line.floorFlat.split(" ")[0] || "" : "",
          flat: line.floorFlat
            ? line.floorFlat.split(" ").slice(1).join(" ") || "Floor 1"
            : "Floor 1",
        };
      });

      // Determine status based on isDraft flag
      const status = isDraft ? "Draft" : "Submitted";

      // Read flowType from URL query parameters
      const searchParams = new URLSearchParams(location.search);
      const flowType = searchParams.get("flowType");

      // Prepare API submission data according to specified format
      const apiPayload = {
        workRequestTitle: data.workRequestTitle || "",
        description: data.description || "",
        siteId: Number(data.siteId),
        projectId: data.projectId ? Number(data.projectId) : null,
        requestFor: data.requestFor || "Construction Work",
        plannedStartDate: data.plannedStartDate
          ? dayjs(data.plannedStartDate).format("YYYY-MM-DD")
          : null,
        plannedEndDate: data.plannedEndDate
          ? dayjs(data.plannedEndDate).format("YYYY-MM-DD")
          : null,
        approvalRequired: !isDraft && data.approvalRequired === "true",
        ...(data.approvalRequired === "true" && { isStrictApproval: Boolean(isStrictApproval) }),
        sourceType:
          data.selectedSource === "Project Management"
            ? "Project Tasks"
            : data.selectedSource === "New Work Line"
              ? "New Work Line"
              : "Work BOQ",
        status: status,
        workLines: transformedWorkLines,
        approvalHierarchy:
          !isDraft && data.approvalRequired === "true" ? approvalHierarchy : [],
        ...(flowType && { flowType: flowType }),
      };

      console.log("Work Request API Payload:", apiPayload);

      // Call the API
      const response: any = await workRequestApis.createWorkRequest(apiPayload);

      if (response && response.status) {
        const successMessage = isDraft
          ? "Work Request saved as draft successfully!"
          : "Work Request submitted successfully!";
        message.success(successMessage);

        // Show success modal with API response information
        Swal.fire({
          title: isDraft
            ? "Work Request Saved as Draft!"
            : "Work Request Submitted Successfully!",
          text: isDraft
            ? "You can continue editing this work request later."
            : "Your work request has been submitted for approval.",
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Go to Work Request List",
          cancelButtonText: "Create Another",
          confirmButtonColor: "#28a745",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed) {
            // Navigate to work request list
            const searchParams = new URLSearchParams(location.search);
            const flowType = searchParams.get("flowType");
            const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
            Navigate(`/work-request-list${flowTypeQuery}`);
          } else {
            // Navigate to create new work request
            const searchParams = new URLSearchParams(location.search);
            const flowType = searchParams.get("flowType");
            const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
            window.location.href = `/work-request-creation${flowTypeQuery}`;
          }
        });
      } else {
        throw new Error(response?.message || "Failed to create work request");
      }
    } catch (error: any) {
      console.error("Error submitting work request:", error);
      const errorMessage = isDraft
        ? "Failed to save work request as draft"
        : "Failed to create work request";
      message.error(errorMessage);
      Swal.fire({
        icon: "error",
        title: isDraft ? "Save Failed" : "Submission Failed",
        text:
          error.message ||
          (isDraft
            ? "Failed to save work request. Please try again."
            : "Failed to submit work request. Please try again."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const ApprovalTimelineContent = () => {
    // Parse the approvalsData string to an object
    let approvalsData;
    try {
      approvalsData = formData?.approvalsData
        ? JSON.parse(formData.approvalsData)
        : null;
    } catch (error) {
      console.error("Error parsing approvalsData:", error);
      approvalsData = null;
    }

    const isStrictMode = formData?.isStrictApproval || false;

    // Check if approvalsData exists and has data array
    if (
      !approvalsData ||
      !approvalsData.data ||
      approvalsData.data.length === 0 ||
      formData?.approvalRequired !== "true"
    ) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status?.toLowerCase() == "approved";
              const isRejected = approval.status?.toLowerCase() == "rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.userName}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                  {approval.approvalStage && (
                    <div className="text-muted small">
                      <Tag
                        color="blue"
                        style={{ fontSize: "10px" }}
                      >
                        {approval.approvalStage}
                      </Tag>
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
              // textDecoration: "underline",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>

          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  return (
    <>
      <div className="content clearfix">
        <div className="card">
          <div className="card-body">
            {/* <div className="card-title">
              <h5>📃 3. Preview Section</h5>
              <p className="text-muted">
                Summarized view of all General Information and Selected Work
                Request Lines
              </p>
            </div> */}

            {/* FSD Preview - Sample Data -- General Information */}
            <Card title="📄 General Information" className="mb-4">
              <Descriptions
                bordered
                column={2}
                size="small"
                labelStyle={{ fontWeight: "bold", width: "200px" }}
              >
                {/* <Descriptions.Item label="Work Request #" span={1}>
                  <strong>
                    {formData.workRequestNumber || "Auto-generated"}
                  </strong>
                </Descriptions.Item> */}
                <Descriptions.Item label="Work Request Title" span={1}>
                  <strong>{formData.workRequestTitle || "-"}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Description" span={1}>
                  {formData.description || "No description provided"}
                </Descriptions.Item>
                <Descriptions.Item label="Site Selection" span={1}>
                  <strong>{formData.siteName || formData.siteId || "-"}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Project Selection" span={1}>
                  {formData.projectName || formData.projectId || "Not selected"}
                </Descriptions.Item>
                <Descriptions.Item label="Request For" span={1}>
                  <Tag color="blue">
                    {getRequestForDisplay(formData.requestFor) || "-"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Approval Required" span={1}>
                  {formData?.approvalsData && (
                    <ApprovalTimelineContent />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Planned Start Date" span={1}>
                  <strong>{formatDate(formData.plannedStartDate)}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Planned End Date" span={1}>
                  <strong>{formatDate(formData.plannedEndDate)}</strong>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* FSD Preview - Sample - Work Request Lines */}
            <Card
              title={`📄 Work Request Lines (${workRequestLines.length} lines)`}
              className="mb-4"
            >
              {workRequestLines.length > 0 ? (
                <Table
                  columns={workLinesColumns}
                  dataSource={workRequestLines}
                  pagination={false}
                  scroll={{ x: 1000 }}
                  size="small"
                  rowKey="id"
                  bordered
                  className="custom-table"
                  expandable={{
                    expandedRowRender,
                    expandIcon: ({ expanded, onExpand, record }) => {
                      const materialAllocation = (record as any)
                        .materialAllocation;
                      const documents = record.documents || [];
                      const taskBoqs = (record as any).taskBoqs || [];
                      const isWorkBOQ =
                        record.source === WorkLineSource.WorkBOQ;
                      const isProjectManagement =
                        record.source === WorkLineSource.ProjectManagement;

                      const hasMaterialBOQ =
                        materialAllocation && materialAllocation.length > 0;
                      const hasDocuments = isWorkBOQ && documents.length > 0;
                      const hasTaskBoqs =
                        isProjectManagement && taskBoqs.length > 0;

                      const hasExpandableContent =
                        hasMaterialBOQ || hasDocuments || hasTaskBoqs;

                      if (!hasExpandableContent) return null;

                      return expanded ? (
                        <DownOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ color: "#1890ff", cursor: "pointer" }}
                        />
                      ) : (
                        <RightOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ color: "#1890ff", cursor: "pointer" }}
                        />
                      );
                    },
                    rowExpandable: (record) => {
                      const materialAllocation = (record as any)
                        .materialAllocation;
                      const documents = record.documents || [];
                      const taskBoqs = (record as any).taskBoqs || [];
                      const isWorkBOQ =
                        record.source === WorkLineSource.WorkBOQ;
                      const isProjectManagement =
                        record.source === WorkLineSource.ProjectManagement;

                      const hasMaterialBOQ =
                        materialAllocation && materialAllocation.length > 0;
                      const hasDocuments = isWorkBOQ && documents.length > 0;
                      const hasTaskBoqs =
                        isProjectManagement && taskBoqs.length > 0;

                      return hasMaterialBOQ || hasDocuments || hasTaskBoqs;
                    },
                  }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={9}>
                        <div className="text-end">
                          <strong>
                            Total Work Lines: {workRequestLines.length}
                          </strong>
                        </div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              ) : (
                <Alert
                  message="No Work Lines Added"
                  description="Please go back to Work Request Details section and add at least one work line."
                  type="warning"
                  showIcon
                />
              )}
            </Card>
          </div>
        </div>

        <div className="actions clearfix">
          <ul>
            <li>
              <Space>
                <Button
                  color="secondary"
                  type="button"
                  onClick={handleSubmit((data) => submitForm(data, true))}
                  loading={submitting}
                  disabled={workRequestLines.length === 0}
                >
                  {isEditMode ? "Update Draft" : "Save as Draft"}
                </Button>
                <Button
                  color="primary"
                  type="button"
                  onClick={handleSubmit((data) => submitForm(data, false))}
                  loading={submitting}
                  disabled={workRequestLines.length === 0}
                >
                  {isEditMode
                    ? "Submit WR"
                    : formData.approvalRequired === "true"
                      ? "Submit for Approval"
                      : "Submit Work Request"}
                </Button>
              </Space>
            </li>
            <li>
              <Button color="light" type="button" onClick={props.prev}>
                {/* <Icon name="arrow-left" className="me-1" /> */}
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default WorkRequestPreview;
